import {
  TAU_MVP_DEFAULT,
  levelFromTotalXp,
  xpToReachLevel,
} from '@renavora/content';
import type { DungeonTemplateId } from '@renavora/shared';

import type { SerializableCharacter } from '../persistence/index.js';

export function jobLevelFromCurrentJobXp(totalXp: number): number {
  return Math.max(1, levelFromTotalXp(Math.max(0, totalXp), TAU_MVP_DEFAULT));
}

export function xpFloorForJobLevel(jobLevel: number): number {
  const pinnedLevel = Math.max(1, Math.floor(jobLevel));
  if (pinnedLevel <= 1) return 0;
  return Math.ceil(xpToReachLevel(pinnedLevel, TAU_MVP_DEFAULT));
}

export function upsertJobHistoryMax(
  jobHistory: SerializableCharacter['jobHistory'],
  jobId: string,
  maxLv: number,
): SerializableCharacter['jobHistory'] {
  const boundedMaxLv = Math.max(1, Math.floor(maxLv));
  const existingIdx = jobHistory.findIndex((h) => h.jobId === jobId);
  return existingIdx >= 0
    ? jobHistory.map((h, i) =>
        i === existingIdx ? { ...h, maxLv: Math.max(h.maxLv, boundedMaxLv) } : h,
      )
    : [...jobHistory, { jobId, maxLv: boundedMaxLv }];
}

export function applyRewardDeltaToCharacter(
  character: SerializableCharacter,
  rewardDelta: { readonly gold: number; readonly xp: number },
): SerializableCharacter {
  const nextXp = Math.max(0, character.xp + rewardDelta.xp);
  const xpLevel = jobLevelFromCurrentJobXp(nextXp);
  const nextJobLevel =
    rewardDelta.xp > 0 ? Math.max(character.jobLevel, xpLevel) : character.jobLevel;
  const nextJobHistory =
    rewardDelta.xp > 0
      ? upsertJobHistoryMax(character.jobHistory, character.jobId, nextJobLevel)
      : character.jobHistory;

  return {
    ...character,
    gold: character.gold + rewardDelta.gold,
    xp: nextXp,
    jobLevel: nextJobLevel,
    jobHistory: nextJobHistory,
  };
}

export function appendCompletedDungeon(
  completedDungeons: SerializableCharacter['completedDungeons'],
  dungeonId: DungeonTemplateId,
): SerializableCharacter['completedDungeons'] {
  return completedDungeons.includes(dungeonId)
    ? completedDungeons
    : [...completedDungeons, dungeonId];
}
